import { useState, type ComponentType } from 'react';
import { ChatWindow } from './ChatWindow';
import type { PartnerConfig, ChatState, CustomerInfo } from '../types';
import type { PendingFile } from './FilePreview';

interface LanguageState {
  current: string;
  cf_language: string | null;
  show_cf_suggestion: boolean;
}

interface ChatWindowProps {
  config: PartnerConfig;
  state: ChatState;
  onSendMessage: (message: string) => void;
  onCustomerSubmit: (customer: Partial<CustomerInfo>) => void;
  onDrawerDismiss: () => void;
  onDrawerOpen: (intent?: string) => void;
  pendingFiles: PendingFile[];
  onFilesSelected: (files: File[]) => void;
  onFileRemove: (id: string) => void;
  // Feature flags
  fileUploadEnabled?: boolean;
  researchModeEnabled?: boolean;
  languageState?: LanguageState;
  onSelectLanguage?: (code: string) => void;
  onDismissLanguageSuggestion?: () => void;
  onVerifyEmail?: () => void;
  onConfirmOtp?: (code: string) => void;
  onResendOtp?: () => void;
  onToggleResearchMode?: (enabled: boolean) => void;
  onUpdateNotificationPrefs?: (prefs: { browser: boolean; email: boolean }) => void;
}

interface FloatingChatProps {
  config: PartnerConfig;
  state: ChatState;
  onSendMessage: (message: string) => void;
  onCustomerSubmit: (customer: Partial<CustomerInfo>) => void;
  onDrawerDismiss: () => void;
  onDrawerOpen: (intent?: string) => void;
  pendingFiles: PendingFile[];
  onFilesSelected: (files: File[]) => void;
  onFileRemove: (id: string) => void;
  // Feature flags
  fileUploadEnabled?: boolean;
  researchModeEnabled?: boolean;
  // Language props
  languageState?: LanguageState;
  onSelectLanguage?: (code: string) => void;
  onDismissLanguageSuggestion?: () => void;
  // Research mode props
  onVerifyEmail?: () => void;
  onConfirmOtp?: (code: string) => void;
  onResendOtp?: () => void;
  onToggleResearchMode?: (enabled: boolean) => void;
  onUpdateNotificationPrefs?: (prefs: { browser: boolean; email: boolean }) => void;
  // UI version support
  ChatWindowComponent?: ComponentType<ChatWindowProps>;
}

export function FloatingChat({
  config,
  state,
  onSendMessage,
  onCustomerSubmit,
  onDrawerDismiss,
  onDrawerOpen,
  pendingFiles,
  onFilesSelected,
  onFileRemove,
  fileUploadEnabled,
  researchModeEnabled,
  languageState,
  onSelectLanguage,
  onDismissLanguageSuggestion,
  onVerifyEmail,
  onConfirmOtp,
  onResendOtp,
  onToggleResearchMode,
  onUpdateNotificationPrefs,
  ChatWindowComponent = ChatWindow,
}: FloatingChatProps) {
  const [isOpen, setIsOpen] = useState(false);

  const primaryColor = config.branding?.colors?.primary || '#2563eb';

  return (
    <>
      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-24 right-4 w-96 h-[600px] max-h-[80vh] rounded-2xl shadow-2xl overflow-hidden z-50 bg-white">
          <ChatWindowComponent
            config={config}
            state={state}
            onSendMessage={onSendMessage}
            onCustomerSubmit={onCustomerSubmit}
            onDrawerDismiss={onDrawerDismiss}
            onDrawerOpen={onDrawerOpen}
            pendingFiles={pendingFiles}
            onFilesSelected={onFilesSelected}
            onFileRemove={onFileRemove}
            fileUploadEnabled={fileUploadEnabled}
            researchModeEnabled={researchModeEnabled}
            languageState={languageState}
            onSelectLanguage={onSelectLanguage}
            onDismissLanguageSuggestion={onDismissLanguageSuggestion}
            onVerifyEmail={onVerifyEmail}
            onConfirmOtp={onConfirmOtp}
            onResendOtp={onResendOtp}
            onToggleResearchMode={onToggleResearchMode}
            onUpdateNotificationPrefs={onUpdateNotificationPrefs}
          />
        </div>
      )}

      {/* Floating Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-4 right-4 w-16 h-16 rounded-full shadow-lg flex items-center justify-center z-50 transition-transform hover:scale-110"
        style={{ backgroundColor: primaryColor }}
        aria-label={isOpen ? 'Close chat' : 'Open chat'}
      >
        {isOpen ? (
          <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        ) : (
          <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
        )}
      </button>
    </>
  );
}
